import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Group,
    HoverCard,
    Stack,
    Text,
    ThemeIcon,
    UnstyledButton,
} from '@mantine/core';
import {
    IconCircleCheck,
    IconBallFootball,
    IconAd,
    IconMoon,
} from '@tabler/icons-react';
import { LiveKpis, OnlineDevice } from '../../data/PingSummary';

type Props = {
    data: LiveKpis | undefined;
};

const STATUS_META: Record<
    OnlineDevice['status'],
    { color: string; label: string }
> = {
    sport: { color: 'green', label: 'Sport' },
    commercial: { color: 'yellow', label: 'Commercial' },
    idle: { color: 'gray', label: 'Idle' },
};

const KpiRow: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: number | string;
    color: string;
    clickable?: boolean;
}> = ({ icon, label, value, color, clickable }) => {
    const prevValue = useRef<number | string>(value);
    const [pulsing, setPulsing] = useState(false);

    useEffect(() => {
        // Only pulse on a real numeric change (skip the initial — → number transition).
        if (
            typeof value === 'number' &&
            typeof prevValue.current === 'number' &&
            value !== prevValue.current
        ) {
            setPulsing(true);
            const t = setTimeout(() => setPulsing(false), 900);
            prevValue.current = value;
            return () => clearTimeout(t);
        }
        prevValue.current = value;
    }, [value]);

    return (
        <Group
            gap="md"
            wrap="nowrap"
            justify="space-between"
            className={pulsing ? 'kpi-row-pulse' : undefined}
            style={
                {
                    cursor: clickable ? 'pointer' : 'default',
                    borderRadius: 6,
                    '--kpi-pulse-color': `var(--mantine-color-${color}-3)`,
                } as React.CSSProperties
            }
        >
            <Group gap="md" wrap="nowrap">
                <ThemeIcon variant="light" color={color} size="xl" radius="md">
                    {icon}
                </ThemeIcon>
                <Text size="md">{label}</Text>
            </Group>
            <Text fw={700} fz={28} lh={1}>
                {value}
            </Text>
        </Group>
    );
};

const PingKpiStrip: React.FC<Props> = ({ data }) => {
    const navigate = useNavigate();
    const onlineDevices = data?.onlineDevices ?? [];

    return (
        <Card shadow="xs" withBorder padding="md" h="100%">
            <Stack gap="md" justify="center" h="100%">
                <HoverCard
                    width={300}
                    shadow="md"
                    openDelay={120}
                    closeDelay={80}
                    position="bottom-start"
                    withinPortal
                    disabled={onlineDevices.length === 0}
                >
                    <HoverCard.Target>
                        <div>
                            <KpiRow
                                icon={<IconCircleCheck size="1.4rem" />}
                                label="Devices online"
                                value={data?.devicesOnline ?? '—'}
                                color="teal"
                                clickable={onlineDevices.length > 0}
                            />
                        </div>
                    </HoverCard.Target>
                    <HoverCard.Dropdown>
                        <Stack gap={4}>
                            <Text size="xs" c="dimmed" fw={500} tt="uppercase">
                                Online now · {data?.devicesOnline ?? 0}
                            </Text>
                            {onlineDevices.map((d) => {
                                const meta = STATUS_META[d.status];
                                const detail =
                                    d.status === 'sport' && d.currentSport
                                        ? d.currentSport
                                        : meta.label;
                                return (
                                    <UnstyledButton
                                        key={d.id}
                                        onClick={() => navigate(`/device/${d.id}`)}
                                        style={{ padding: '4px 6px', borderRadius: 4 }}
                                        className="hover-row"
                                    >
                                        <Group gap="xs" wrap="nowrap" justify="space-between">
                                            <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                                                <span
                                                    style={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        background: `var(--mantine-color-${meta.color}-6)`,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Text size="sm" truncate>
                                                    {d.name}
                                                </Text>
                                            </Group>
                                            <Text size="xs" c="dimmed" truncate>
                                                {detail}
                                            </Text>
                                        </Group>
                                    </UnstyledButton>
                                );
                            })}
                        </Stack>
                    </HoverCard.Dropdown>
                </HoverCard>
                <KpiRow
                    icon={<IconBallFootball size="1.4rem" />}
                    label="Showing sports"
                    value={data?.showingSports ?? '—'}
                    color="green"
                />
                <KpiRow
                    icon={<IconAd size="1.4rem" />}
                    label="In commercials"
                    value={data?.showingCommercials ?? '—'}
                    color="yellow"
                />
                <KpiRow
                    icon={<IconMoon size="1.4rem" />}
                    label="Idle"
                    value={data?.idle ?? '—'}
                    color="gray"
                />
            </Stack>
        </Card>
    );
};

export default PingKpiStrip;
